<template>
  <div class="dropdown wc-login-menu-v2" :class="{ show }" @keydown.esc="hide">
    <button
      id="login-menu-v2"
      ref="loginMenuV2Trigger"
      class="unstyled-btn wc-login-menu-v2__trigger"
      type="button"
      :aria-label="$t('signIn')"
      aria-haspopup="true"
      :aria-expanded="show"
      data-testid="login-menu-v2-trigger"
      @click="toggle"
    >
      <WCSvgIcon name="user" class="wc-login-menu-v2__icon" />
      <span
        class="wc-login-menu-v2__label"
        :class="{ 'wc-login-menu-v2__label--signed-in': isSignedIn }"
      >
        {{ $t('signIn') }}&nbsp;<WCSvgIcon
          name="chevron-down"
          :icon-size="{ widthRem: 0.6875, heightRem: 0.375 }"
          class="wc-login-menu-v2__chevron"
        />
      </span>
    </button>
    <Teleport to="body">
      <div
        v-show="show"
        ref="loginMenuV2Dropdown"
        class="wc-login-menu-v2__dropdown"
        :style="dropdownStyle"
        role="menu"
        aria-labelledby="login-menu-v2"
      >
        <router-link
          class="wc-login-menu-v2__dropdown-item"
          :to="{ name: 'Login' }"
          data-testid="login-link"
          @click="hide"
        >
          {{ $t('signIn') }}
        </router-link>
        <router-link
          class="wc-login-menu-v2__dropdown-item"
          :to="{ name: 'Register' }"
          data-testid="register-link"
          v-show="$configuration.allowCustomerAdd"
          @click="hide"
        >
          {{ $t('register') }}
        </router-link>
      </div>
      <div v-show="show" class="wc-login-menu-v2__backdrop" @click="hide" />
    </Teleport>
  </div>
</template>

<script>
export default {
  props: {
    isSignedIn: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      show: false,
      dropdownStyle: {},
    };
  },
  methods: {
    async toggle() {
      if (!this.show) {
        this.show = true;
        await this.$nextTick();
        this.updateDropdownPosition();
      } else {
        this.show = false;
      }
    },
    hide() {
      this.show = false;
    },
    updateDropdownPosition() {
      const trigger = this.$refs.loginMenuV2Trigger;
      const dropdown = this.$refs.loginMenuV2Dropdown;
      if (!trigger || !dropdown) return;

      const container = trigger.closest('.wc-login-menu-v2') || trigger.parentElement;
      const rect = container.getBoundingClientRect();
      const dropdownWidth = dropdown.offsetWidth;
      const viewportPadding = 8;
      const triggerWidth = rect.width || trigger.offsetWidth || 0;
      const centeredLeft = rect.left + (triggerWidth - dropdownWidth) / 2;
      let left;
      if (centeredLeft + dropdownWidth + viewportPadding > window.innerWidth) {
        left = window.innerWidth - dropdownWidth;
      } else {
        left = Math.min(
          Math.max(viewportPadding, centeredLeft),
          window.innerWidth - dropdownWidth - viewportPadding,
        );
      }

      this.dropdownStyle = {
        position: 'fixed',
        top: `${rect.bottom}px`,
        left: `${left}px`,
        right: 'auto',
        zIndex: 10000,
      };
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-login-menu-v2 {
  border: 2px solid transparent;
  border-radius: convert-px-to-rem(5) convert-px-to-rem(5) 0 0;
  min-width: convert-px-to-rem(100);
  transition: border-color 0.15s ease;

  &.show {
    border-color: var(--primary-lighter-9, var(--primary));
  }
}

.wc-login-menu-v2__trigger {
  appearance: none;
  background: transparent;
  border: 0;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: convert-px-to-rem(4) convert-px-to-rem(7);
  margin-right: convert-px-to-rem(18);
  color: var(--dark-lighter-3);
}

.wc-login-menu-v2__icon {
  font-size: $font-size-xl;
  color: var(--primary, $primary);
}

.wc-login-menu-v2__label {
  margin-top: convert-px-to-rem(2);
  font-size: $font-size-sm;
  line-height: 1.2;
  font-weight: 400;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: convert-px-to-rem(3);
}

.wc-login-menu-v2__label--signed-in {
  font-weight: 700;
}

.wc-login-menu-v2__chevron {
  color: var(--body-color, $body-color);
  transition: transform 0.3s ease;

  .show & {
    transform: rotate(180deg);
  }
}

.wc-login-menu-v2__dropdown {
  background: var(--white, $white);
  border: 0.5px solid var(--dark-lighter-5);
  border-radius: 0 0 0.375rem 0.375rem;
  padding: 0.5rem 0;
  width: convert-px-to-rem(100);
  box-shadow: 0 0.25rem 0.25rem 0 rgba(0, 0, 0, 0.15);
}

.wc-login-menu-v2__dropdown-item {
  display: flex;
  justify-content: center;
  width: 100%;
  padding: convert-px-to-rem(2) convert-px-to-rem(28) convert-px-to-rem(5) convert-px-to-rem(28);
  color: var(--gray-900, $gray-900);
  text-decoration: none;
  white-space: nowrap;
  font-size: 1rem;
  line-height: 1.5;

  &:hover,
  &:focus {
    background-color: $gray-100;
    color: var(--gray-900, $gray-900);
  }
}

.wc-login-menu-v2__backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
}
</style>
